import { RefObject, useEffect, useCallback, useState } from 'react';
import MousePosition from 'ol/control/MousePosition';
import { useTranslation } from 'react-i18next';

import { useMapStore} from '../stores/useMapStore';
import {useLine3DNavigationStore} from '../stores/useLine3DNavigationStore';
import {LineType} from '../models/enums/LineType';
import {useSeismicStore} from '../stores/useSeismicStore';
import {AmplitudeDomain} from '../models/enums/AmplitudeDomain';
import { getTrace, getAmplitude, getZSliceY } from '../utils/calculateScreenValuesUtils';
import { ILineString } from '../models/interfaces/ILineString';
import { SurveyType } from '../models/enums/SurveyType';
import { useSessionStore } from 'session/useSessionStore';

export function useMousePosition (ref: RefObject<HTMLDivElement>){
    const map = useMapStore(state => state.map);
    const { t } = useTranslation();
    const lineType = useLine3DNavigationStore(state => state.lineType);
    const [ control, setControl ] = useState<MousePosition | null>(null);
    const { tenantConfig } = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
    }));
    const { calculator, surveyMetadata, scaleY, scaleX, restrict, isInverted, isVerticallyInverted, dataGeometryLine, sortOption } = useSeismicStore(state => ({
        calculator: state.calculator,
        restrict: state.restrict,
        scaleX: state.scale.x,
        scaleY: state.scale.y,
        surveyMetadata: state.surveyMetadata,
        isInverted: state.isInverted,
        isVerticallyInverted: state.isVerticallyInverted,
        dataGeometryLine: state.geometryLine,
        sortOption: state.sortOption
    }));

    const coordinateFormat = useCallback((coordinate: number[] | undefined) => {
        //console.log('coord --->', coordinate);
        if (!coordinate || !surveyMetadata || !calculator || !restrict){
            return '';
        }

        const increment = calculator.getLineIncrement(surveyMetadata);
        const start = calculator.getMinTrace(surveyMetadata);
        const end = calculator.getMaxTrace(surveyMetadata);

        let trace = getTrace(coordinate[0], scaleX, start, increment);

        if (isInverted) {
            trace = end - trace + start;
        }

        let amplitude = lineType === LineType.ZSlice ? getZSliceY(coordinate[1], scaleY, surveyMetadata.Survey3DInfo.XlineIncrement, surveyMetadata.Survey3DInfo.XlineStart) : parseInt(getAmplitude(coordinate[1], scaleY, calculator!.calculateSampleInterval(surveyMetadata)));

        if (isVerticallyInverted) {
            amplitude = surveyMetadata.Survey3DInfo.XlineEnd - amplitude + surveyMetadata.Survey3DInfo.XlineStart;
        }

        let labelX = (lineType === LineType.Inline)?'Xline':'Inline';
        let realTrace = trace;
        if (!surveyMetadata.IsIndexed || surveyMetadata.Type === SurveyType.Seismic2D){
            labelX = 'Trace';
            if (tenantConfig?.viewer2DDefaults?.label_field === 'shot' && surveyMetadata.Survey2DInfo?.ShotPointStart > 0 && surveyMetadata.Survey2DInfo?.ShotPointEnd > 0 ) {
                labelX = 'Shot';
                if (!isInverted) {
                    trace = surveyMetadata.Survey2DInfo?.ShotPointStart + parseInt((trace * surveyMetadata.Survey2DInfo?.ShotPointIncrement).toFixed(0));
                } else {
                    trace = surveyMetadata.Survey2DInfo?.ShotPointEnd - parseInt((trace * surveyMetadata.Survey2DInfo?.ShotPointIncrement).toFixed(0));
                }
                if (trace < surveyMetadata.Survey2DInfo?.ShotPointStart) {
                    trace = surveyMetadata.Survey2DInfo?.ShotPointStart;
                }
                if (trace > surveyMetadata.Survey2DInfo?.ShotPointEnd) {
                    trace = surveyMetadata.Survey2DInfo?.ShotPointEnd;
                }
            }
        }

        const labelY = lineType === LineType.ZSlice ?
            'Xline'
            :
            (surveyMetadata.Domain === AmplitudeDomain.Depth) ?
                t('depth')
                :
                t('time');

        amplitude = amplitude - (Math.abs(surveyMetadata.Datum ?? 0)) + (surveyMetadata.Delay ?? 0);

        const finalX = (restrict.SeismicDisplayTraces)?`<p title='Trace: ${realTrace}' class="x">${labelX}: ${trace}</p>`: '';
        const finalY = (restrict.SeismicDisplayDepth)?`<p class="y">${labelY}: ${amplitude}</p>`: '';

        const segmentOption = sortOption.split(',')[0];
        let segmentLabel = '';

        switch (segmentOption) {
            case 'shotpoint':
                segmentLabel = 'Shot';
                break;
            case 'cdp':
                segmentLabel = 'CDP';
                break;
            case 'channel_number':
                segmentLabel = 'Channel';
                break;
        }

        let segmentHTML = '<p class="segment"></p>';

        if (!!dataGeometryLine?.Segments) {
            const segment = dataGeometryLine.Segments.find(segment => realTrace >= segment.StartTrace && realTrace <= segment.EndTrace);
            if (!!segment?.SegmentKey) {
                segmentHTML = `<p class="segment">${segmentLabel}: ${segment.SegmentKey} </p>`;
            }
        }

        return `${finalX}
        ${segmentHTML}
        ${finalY}`;
    }, [surveyMetadata, calculator, restrict, scaleX, isInverted, lineType, scaleY, t, sortOption, dataGeometryLine]);

    useEffect(() => {
        if (map && !control){
            const control = new MousePosition({
                coordinateFormat: coordinateFormat,
                target: ref.current as HTMLDivElement,
                className: 'geopost-seismic-mouse-position'
            });

            map.addControl(control);

            setControl(control);
        }
    }, [map?.geopostKey]);

    useEffect(() => {
        if (control){
            control.setCoordinateFormat(coordinateFormat);
        }
    }, [coordinateFormat]);
}